import { getConfigOptional } from '../config';
import { fetchAPI } from '../api';
//import type { FilterConfig, FilterOption, FilterApiSource } from './types';
import type { FilterConfig, FilterOption } from './types';
import {
  getFilterValue,
  setFilterOptions,
//  getFilterOptions,
  setFilterLoading,
  resetFilter
} from './filter-state';

// ============================================================================
// Получение конфигурации фильтров
// ============================================================================

export function getFiltersConfig(): FilterConfig[] {
  const config = getConfigOptional('filters');
  return config?.filters ?? [];
}

export function getFilterConfig(filterId: string): FilterConfig | undefined {
  return getFiltersConfig().find(f => f.id === filterId);
}

// ============================================================================
// Шаблонизация label
// ============================================================================

/**
 * Форматирует дату в формат DD.MM.YYYY
 */
function formatDate(value: string | Date): string {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(date.getTime())) return String(value);
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Извлекает значение из объекта по пути (поддерживает вложенные поля через точку).
 * Пример: getNestedValue(obj, 'vessel.name') → obj.vessel.name
 */
function getNestedValue(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current === null || current === undefined) return '';
    current = current[part];
  }
  return current ?? '';
}

/**
 * Применяет шаблон к объекту.
 * Поддерживает:
 * - {field} — простое поле
 * - {field|date} — форматирование даты
 * - {field.nested} — вложенные поля
 * 
 * Пример: "{date_start|date} - {num} - {vessel.name}"
 */
function applyTemplate(template: string, item: any): string {
  return template.replace(/\{([^}]+)\}/g, (_match, expr) => {
    const parts = expr.split('|');
    const fieldPath = parts[0].trim();
    const formatter = parts[1]?.trim();
    
    let value = getNestedValue(item, fieldPath);
    
    // Применяем форматтер, если указан
    if (formatter === 'date' && value) {
      value = formatDate(value);
    }
    
    return value === null || value === undefined ? '' : String(value);
  });
}

// ============================================================================
// Построение URL с подстановкой значений родительских фильтров
// ============================================================================

function buildUrl(urlTemplate: string, values: Record<string, any>): string {
  return urlTemplate.replace(/\{(\w+)\}/g, (_, filterId) => {
    const value = values[filterId];
    if (value === null || value === undefined) return '';
    return String(value);
  });
}

// ============================================================================
// Построение параметров запроса
// ============================================================================

function buildParams(
  paramsMapping: Record<string, string> | undefined,
  values: Record<string, any>
): Record<string, string> {
  if (!paramsMapping) return {};

  const params: Record<string, string> = {};

  for (const [paramName, valueRef] of Object.entries(paramsMapping)) {
    if (valueRef.startsWith('$')) {
      const filterId = valueRef.slice(1);
      const value = values[filterId];

      if (value === null || value === undefined) continue;

      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        params[paramName] = value.join(',');
      } else {
        params[paramName] = String(value);
      }
    }
  }

  return params;
}

// ============================================================================
// Загрузка опций фильтра
// ============================================================================

export async function loadFilterOptions(filterId: string): Promise<void> {
  const filterConfig = getFilterConfig(filterId);
  if (!filterConfig) return;

  const source = filterConfig.source;
  if (source.type !== 'api') return;

  const dependsOn = source.dependsOn ?? [];
  const currentValues: Record<string, any> = {};

  console.log(`🔍 Загрузка опций для фильтра: ${filterId}`);
  console.log(`  Зависит от:`, dependsOn);

for (const parentFilterId of dependsOn) {
  const parentValue = getFilterValue(parentFilterId);
  const parentConfig = getFilterConfig(parentFilterId);
  const parentType = parentConfig?.type;
  
  console.log(`  Значение родителя ${parentFilterId} (тип: ${parentType}):`, parentValue);
  
  // Для single-select: null/undefined = не выбрано → блокируем
  if (parentType === 'single-select') {
    if (parentValue === null || parentValue === undefined || parentValue === '') {
      console.log(`  ⚠️ Родительский single-select фильтр не выбран — очищаем опции`);
      setFilterOptions(filterId, []);
      return;
    }
    currentValues[parentFilterId] = parentValue;
    continue;
  }
  
  // Для multiselect: null/undefined = ещё не инициализирован → блокируем
  //                  [] = инициализирован, но ничего не выбрано → пропускаем (не блокируем)
  if (parentType === 'multiselect') {
    if (parentValue === null || parentValue === undefined) {
      console.log(`  ⚠️ Родительский multiselect фильтр не инициализирован — очищаем опции`);
      setFilterOptions(filterId, []);
      return;
    }
    if (Array.isArray(parentValue) && parentValue.length === 0) {
      console.log(`  ℹ️ Родительский multiselect фильтр пустой — пропускаем (не добавляем в параметры)`);
      continue; // Не добавляем в currentValues, но не блокируем загрузку
    }
    currentValues[parentFilterId] = parentValue;
    continue;
  }
  
  // Неизвестный тип — обрабатываем как single-select
  if (parentValue === null || parentValue === undefined) {
    console.log(`  ⚠️ Родительский фильтр не заполнен — очищаем опции`);
    setFilterOptions(filterId, []);
    return;
  }
  currentValues[parentFilterId] = parentValue;
}

  setFilterLoading(filterId, true);

  try {
    let url = buildUrl(source.url, currentValues);
    console.log(`  📡 URL шаблона: ${source.url}`);
    console.log(`  📡 URL после подстановки: ${url}`);

    const params = buildParams(source.paramsMapping, currentValues);
    if (Object.keys(params).length > 0) {
      const qs = new URLSearchParams(params).toString();
      url += (url.includes('?') ? '&' : '?') + qs;
      console.log(`  📡 Параметры запроса:`, params);
    }

    console.log(`  📡 Финальный URL: ${url}`);

    const data = await fetchAPI<any>(url);
    console.log(`  📥 Ответ сервера:`, data);

    // Универсальное извлечение массива из разных структур ответа
    let items: any[];

    if (Array.isArray(data)) {
      items = data;
    } else if (data && Array.isArray(data.data)) {
      items = data.data;
    } else if (data && Array.isArray(data.years)) {
      items = data.years;
    } else if (data && Array.isArray(data.items)) {
      items = data.items;
    } else if (data && Array.isArray(data.results)) {
      items = data.results;
    } else {
      console.warn(`⚠️ Неожиданная структура ответа для фильтра ${filterId}:`, data);
      setFilterOptions(filterId, []);
      return;
    }

    console.log(`  📋 Извлечённый массив items:`, items);

    // Преобразуем в опции
    const options: FilterOption[] = items.map((item: any) => {
      if (typeof item !== 'object' || item === null) {
        return {
          value: String(item),
          label: String(item)
        };
      }
      
      if (source.labelTemplate) {
        return {
          value: String(item[source.valueField]),
          label: applyTemplate(source.labelTemplate, item)
        };
      }
      
      return {
        value: String(item[source.valueField]),
        label: String(item[source.labelField])
      };
    });

    console.log(`  ✅ Созданные опции:`, options);

    setFilterOptions(filterId, options);
  } catch (error) {
    console.error(`❌ Ошибка загрузки опций фильтра ${filterId}:`, error);
    setFilterOptions(filterId, []);
  } finally {
    setFilterLoading(filterId, false);
  }
}

// ============================================================================
// Обработка изменения значения фильтра
// ============================================================================

/**
 * Вызывается при изменении значения фильтра.
 * Выполняет каскадный сброс зависимых фильтров и перезагрузку их опций.
 */
export async function onFilterValueChanged(filterId: string): Promise<void> {
  const filterConfig = getFilterConfig(filterId);
  if (!filterConfig) return;

  const affectedFilterIds = filterConfig.affects ?? [];

  // Каскадно сбрасываем зависимые фильтры
  for (const affectedId of affectedFilterIds) {
    resetFilter(affectedId);
    // Рекурсивно сбрасываем фильтры, которые зависят от сброшенного
    await onFilterValueChanged(affectedId);
  }

  // Перезагружаем опции всех фильтров, которые зависят от изменённого
  const allFilters = getFiltersConfig();
  for (const filter of allFilters) {
    const source = filter.source;
    if (source.type === 'api' && source.dependsOn?.includes(filterId)) {
      await loadFilterOptions(filter.id);
    }
  }
}